import { useState, type FormEvent, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import BaliHolidayNote from "@/components/feature/BaliHolidayNote";
import DatePicker, { toISODate } from "@/components/base/DatePicker";
import NationalitySelect from "@/components/base/NationalitySelect";
import { submitMultidayInquiry } from "@/lib/villaApi";
import { img } from "@/lib/images";
import { usePriceMap } from "@/hooks/usePrices";
import { useTourBlockedDates, isTourRangeBlocked } from "@/hooks/useTourBlockedDates";
import { LocalizedLink } from "@/i18n/language";

type Status = { type: "idle" | "sending" | "success" | "error"; msg: string };

const TOUR_OPTIONS = [
  { value: "", label: "Bitte wählen …" },
  { value: "Tour 8 – Bali Natur & Künstler", label: "Tour 8 – Bali Natur & Künstler" },
  { value: "Tour 9 – Impressionen Balis", label: "Tour 9 – Impressionen Balis" },
  { value: "Tour 10 – Klassische Bali Rundreise", label: "Tour 10 – Klassische Bali Rundreise" },
  { value: "Tour 11 – Bali Rundreise mit Batur", label: "Tour 11 – Bali Rundreise mit Batur" },
  { value: "Tour 12 – Rundreise mit Kulturprogramm", label: "Tour 12 – Rundreise mit Kulturprogramm" },
];

const TOUR_OPTIONS_EN = [
  { value: "", label: "Please select …" },
  { value: "Tour 8 – Bali Natur & Künstler", label: "Tour 8 – Bali Nature & Artists" },
  { value: "Tour 9 – Impressionen Balis", label: "Tour 9 – Impressions of Bali" },
  { value: "Tour 10 – Klassische Bali Rundreise", label: "Tour 10 – Classic Bali Round Trip" },
  { value: "Tour 11 – Bali Rundreise mit Batur", label: "Tour 11 – Bali Round Trip with Batur" },
  { value: "Tour 12 – Rundreise mit Kulturprogramm", label: "Tour 12 – Round Trip with Culture Program" },
];

const toEnglishTourName = (value: string): string =>
  TOUR_OPTIONS_EN.find((o) => o.value === value)?.label || value;

const ROUNDTRIP_KEYS: Record<string, { adult: string; child: string }> = {
  "Tour 8 – Bali Natur & Künstler": { adult: "roundtrip_8_adult", child: "roundtrip_8_child" },
  "Tour 9 – Impressionen Balis": { adult: "roundtrip_9_adult", child: "roundtrip_9_child" },
  "Tour 10 – Klassische Bali Rundreise": { adult: "roundtrip_10_adult", child: "roundtrip_10_child" },
  "Tour 11 – Bali Rundreise mit Batur": { adult: "roundtrip_11_adult", child: "roundtrip_11_child" },
  "Tour 12 – Rundreise mit Kulturprogramm": { adult: "roundtrip_12_adult", child: "roundtrip_12_child" },
};

const TOUR_DURATIONS: Record<string, number> = {
  "Tour 8 – Bali Natur & Künstler": 2,
  "Tour 9 – Impressionen Balis": 3,
  "Tour 10 – Klassische Bali Rundreise": 4,
  "Tour 11 – Bali Rundreise mit Batur": 4,
  "Tour 12 – Rundreise mit Kulturprogramm": 5,
};

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return toISODate(new Date(y, m - 1, d + days));
}

export default function BookingMultidayPage() {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });
  const { priceMap, money } = usePriceMap();
  const tourBlockedDates = useTourBlockedDates();

  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [tourValue, setTourValue] = useState("");
  const [arrival, setArrival] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  useEffect(() => {
    const tourParam = searchParams.get("tour");
    if (tourParam) {
      const matched = TOUR_OPTIONS.find((o) => o.value.toLowerCase() === tourParam.toLowerCase());
      if (matched) {
        setTourValue(matched.value);
      } else {
        setTourValue(tourParam);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (arrival && start && start < arrival) setStart("");
    if (arrival && end && end < arrival) setEnd("");
  }, [arrival, start, end]);

  useEffect(() => {
    const days = tourValue ? TOUR_DURATIONS[tourValue] : undefined;
    if (start && days) {
      setEnd(addDays(start, days - 1));
    }
  }, [start, tourValue]);

  const today = new Date().toISOString().split("T")[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);
  const maxDateStr = maxDate.toISOString().split("T")[0];

  const tourOptions = isDe ? TOUR_OPTIONS : TOUR_OPTIONS_EN;
  const roundtripKey = tourValue ? ROUNDTRIP_KEYS[tourValue] : undefined;
  const adultPrice = roundtripKey ? priceMap[roundtripKey.adult] ?? 0 : 0;
  const childPrice = roundtripKey ? priceMap[roundtripKey.child] ?? 0 : 0;
  const totalPrice = adults * adultPrice + children * childPrice;
  const tourDays = tourValue ? TOUR_DURATIONS[tourValue] : undefined;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem("phone_alt") as HTMLInputElement | null)?.value.trim() ?? "";
    if (honeypot) {
      setStatus({ type: "success", msg: t("form_success") });
      form.reset();
      setAdults(2);
      setChildren(0);
      setTourValue("");
      setArrival("");
      setStart("");
      setEnd("");
      return;
    }

    const fd = new FormData(form);
    fd.delete("phone_alt");

    const body: Record<string, unknown> = {};
    fd.forEach((value, key) => {
      if (typeof value === "string") body[key] = value;
    });

    const required: Array<[string, string]> = [
      ["tour", isDe ? "Gewünschte Rundreise" : "Desired round trip"],
      ["arrival", isDe ? "Ankunftsdatum in Bali" : "Arrival date in Bali"],
      ["start", isDe ? "Start der Rundreise" : "Start of the round trip"],
      ["hotel", isDe ? "Hotel in Bali" : "Hotel in Bali"],
      ["firstname", isDe ? "Vorname(n)" : "First name(s)"],
      ["lastname", isDe ? "Familienname(n)" : "Last name(s)"],
      ["nationality", isDe ? "Nationalität" : "Nationality"],
      ["guidelang", isDe ? "Sprache für Reiseleitung" : "Guide language"],
      ["email", isDe ? "E-Mail-Adresse" : "Email address"],
      ["phone", isDe ? "Telefon / WhatsApp" : "Phone / WhatsApp"],
    ];
    for (const [key, label] of required) {
      if (!String(body[key] ?? "").trim()) {
        setStatus({
          type: "error",
          msg: isDe
            ? `Bitte füllen Sie das Pflichtfeld „${label}" aus.`
            : `Please fill in the required field "${label}".`,
        });
        return;
      }
    }
    if (!body.terms || !body.privacy) {
      setStatus({
        type: "error",
        msg: isDe
          ? "Bitte akzeptieren Sie die AGB und die Datenschutzerklärung."
          : "Please accept the terms and conditions and the privacy policy.",
      });
      return;
    }

    if (isTourRangeBlocked(tourBlockedDates, start, end || start)) {
      setStatus({
        type: "error",
        msg: isDe
          ? "In diesem Zeitraum finden keine Touren statt. Bitte wählen Sie einen anderen Zeitraum."
          : "No tours take place during this period. Please choose another period.",
      });
      return;
    }

    setStatus({ type: "sending", msg: "" });
    try {
      const { error } = await submitMultidayInquiry({
        tour: isDe ? String(body.tour ?? "") : toEnglishTourName(String(body.tour ?? "")),
        arrival: String(body.arrival ?? ""),
        start: String(body.start ?? ""),
        end: String(body.end ?? ""),
        adults: String(body.adults ?? ""),
        children: String(body.children ?? ""),
        hotel: String(body.hotel ?? ""),
        operator: (body.operator as string) || null,
        lastname: String(body.lastname ?? "").toUpperCase(),
        firstname: String(body.firstname ?? ""),
        nationality: String(body.nationality ?? ""),
        guidelang: String(body.guidelang ?? ""),
        email: String(body.email ?? ""),
        phone: (body.phone as string) || null,
        message: (body.message as string) || null,
        lang: isDe ? "de" : "en",
      });

      if (error) {
        setStatus({ type: "error", msg: error.message || t("form_error") });
        return;
      }

      setStatus({ type: "success", msg: t("booking_form_success") });
      form.reset();
      setAdults(2);
      setChildren(0);
      setTourValue("");
      setArrival("");
      setStart("");
      setEnd("");
    } catch {
      setStatus({ type: "error", msg: t("form_error") });
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors";

  const labelClass = "block text-sm font-medium text-foreground-800 mb-1.5";

  const sectionTitleClass = "font-heading text-xl text-foreground-950 mt-8 mb-4 pb-2 border-b border-background-200";

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={isDe ? "Rundreise" : "Round Trip"}
          title={isDe ? "Rundreise buchen" : "Book a Round Trip"}
          subtitle={isDe
            ? "Reservieren Sie Ihre Bali Rundreise bei Bali VW Tour. Wir melden uns so schnell wie möglich, in der Regel innerhalb von 24 Stunden, persönlich per E-Mail."
            : "Reserve your Bali round trip at Bali VW Tour. We will get back to you as soon as possible, usually within 24 hours, by email."}
          image={img("activities/booking/header-tour-bookings-02.jpg")}
          imageAlt="Bali VW Tour Round Trip Booking"
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-10">
              <form onSubmit={handleSubmit} noValidate>

                {/* ── Ihre Buchung ── */}
                <h2 className={sectionTitleClass}>
                  {isDe ? "Ihre Buchung" : "Your Booking"}
                </h2>

                <div className="space-y-5">
                  {/* Gewünschte Rundreise */}
                  <div>
                    <label htmlFor="booking_tour" className={labelClass}>
                      {isDe ? "Gewünschte Rundreise" : "Desired Round Trip"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <select
                      id="booking_tour"
                      name="tour"
                      required
                      value={tourValue}
                      onChange={(e) => setTourValue(e.target.value)}
                      className={`${inputClass} cursor-pointer`}
                    >
                      {tourOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {tourDays && (
                      <p className="mt-2 text-xs text-foreground-500">
                        {isDe
                          ? `Dauer: ${tourDays} Tage / ${tourDays - 1} Nächte`
                          : `Duration: ${tourDays} days / ${tourDays - 1} nights`}
                      </p>
                    )}
                    {tourValue && (
                      <p className="mt-2 text-xs text-foreground-600">
                        {!roundtripKey
                          ? t("tour_detail_price_on_request")
                          : isDe
                          ? `Einzelpreis: ${money(adultPrice)} pro Erwachsener · ${money(childPrice)} pro Kind (unter 12)`
                          : `Price per person: ${money(adultPrice)} per adult · ${money(childPrice)} per child (under 12)`}
                      </p>
                    )}
                  </div>

                  {/* Ankunftsdatum */}
                  <div>
                    <label htmlFor="booking_arrival" className={labelClass}>
                      {isDe ? "Ankunftsdatum in Bali" : "Arrival date in Bali"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <DatePicker
                      name="arrival"
                      value={arrival}
                      min={today}
                      max={maxDateStr}
                      isDe={isDe}
                      placeholder={isDe ? "Datum wählen" : "Select date"}
                      onChange={setArrival}
                    />
                  </div>

                  {/* Start der Rundreise */}
                  <div>
                    <label htmlFor="booking_start" className={labelClass}>
                      {isDe ? "Start der Rundreise" : "Start of the round trip"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <DatePicker
                      name="start"
                      value={start}
                      min={arrival || today}
                      max={maxDateStr}
                      disabledDates={tourBlockedDates}
                      isDe={isDe}
                      placeholder={isDe ? "Datum wählen" : "Select date"}
                      onChange={setStart}
                    />
                    <BaliHolidayNote isDe={isDe} blockedDates={tourBlockedDates} />
                  </div>

                  {/* Ende der Rundreise */}
                  <div>
                    <label htmlFor="booking_end" className={labelClass}>
                      {isDe ? "Ende der Rundreise" : "End of the round trip"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <DatePicker
                      name="end"
                      value={end}
                      min={start || arrival || today}
                      max={maxDateStr}
                      isDe={isDe}
                      disabled
                      placeholder={isDe ? "Automatisch berechnet" : "Auto-calculated"}
                      onChange={setEnd}
                    />
                    {tourDays && (
                      <p className="mt-2 text-xs text-foreground-500">
                        {isDe
                          ? "Wird automatisch anhand der Tour-Dauer berechnet"
                          : "Automatically calculated based on tour duration"}
                      </p>
                    )}
                  </div>

                  {/* Anzahl Erwachsene */}
                  <div>
                    <label className={labelClass}>
                      {isDe ? "Anzahl Erwachsene" : "Number of adults"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setAdults((a) => Math.max(2, a - 1))}
                        className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <input
                        name="adults"
                        type="number"
                        min={2}
                        max={50}
                        value={adults}
                        onChange={(e) => setAdults(Math.max(2, Math.min(50, parseInt(e.target.value) || 2)))}
                        className="w-20 text-center px-2 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      />
                      <button
                        type="button"
                        onClick={() => setAdults((a) => Math.min(50, a + 1))}
                        className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-foreground-600">
                      {isDe ? "Mindestens 2 Erwachsene" : "Minimum 2 adults"}
                    </p>
                  </div>

                  {/* Anzahl Kinder */}
                  <div>
                    <label className={labelClass}>
                      {isDe ? "Anzahl Kinder (unter 12 Jahre)" : "Number of children (under 12)"}
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setChildren((c) => Math.max(0, c - 1))}
                        className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <input
                        name="children"
                        type="number"
                        min={0}
                        max={50}
                        value={children}
                        onChange={(e) => setChildren(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))}
                        className="w-20 text-center px-2 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      />
                      <button
                        type="button"
                        onClick={() => setChildren((c) => Math.min(50, c + 1))}
                        className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>

                  {/* Gesamtpreis */}
                  {tourValue && roundtripKey && (
                    <div className="flex items-center justify-between bg-background-100 border border-background-200 rounded-lg px-5 py-4">
                      <div className="flex items-center gap-2">
                        <i className="ri-price-tag-3-line text-accent-700"></i>
                        <span className="text-sm font-medium text-foreground-800">
                          {isDe ? "Gesamtpreis" : "Total price"}
                        </span>
                      </div>
                      <span className="font-heading text-lg font-semibold text-accent-700">
                        {money(totalPrice)}
                      </span>
                    </div>
                  )}

                  {/* Hotel */}
                  <div>
                    <label htmlFor="booking_hotel" className={labelClass}>
                      {isDe ? "Hotel in Bali" : "Hotel in Bali"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="booking_hotel"
                      name="hotel"
                      type="text"
                      required
                      placeholder={isDe ? "z. B. Villa Kompiang" : "e.g. Villa Kompiang"}
                      className={inputClass}
                    />
                  </div>

                  {/* Reiseveranstalter */}
                  <div>
                    <label htmlFor="booking_operator" className={labelClass}>
                      {isDe ? "Reiseveranstalter (optional)" : "Tour operator (optional)"}
                    </label>
                    <input
                      id="booking_operator"
                      name="operator"
                      type="text"
                      placeholder={isDe ? "z. B. TUI, Booking.com, etc." : "e.g. TUI, Booking.com, etc."}
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* ── Ihre Angaben ── */}
                <h2 className={sectionTitleClass}>
                  {isDe ? "Ihre Angaben" : "Your Details"}
                </h2>
                <p className="mb-5 -mt-2 text-sm text-foreground-600">
                  {isDe
                    ? <>Bitte bei zwei verschiedenen Namen immer beide angeben.<br />Max / Erika&nbsp;&nbsp;&nbsp;&nbsp;MUSTERMANN / MÜLLER</>
                    : <>If there are two different names, please always provide both.<br />John / Jane&nbsp;&nbsp;&nbsp;&nbsp;DOE / SMITH</>}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Vorname */}
                  <div>
                    <label htmlFor="booking_firstname" className={labelClass}>
                      {isDe ? "Vorname(n)" : "First name(s)"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="booking_firstname"
                      name="firstname"
                      type="text"
                      required
                      className={inputClass}
                    />
                  </div>

                  {/* Nachname */}
                  <div>
                    <label htmlFor="booking_lastname" className={labelClass}>
                      {isDe ? "Familienname(n)" : "Last name(s)"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="booking_lastname"
                      name="lastname"
                      type="text"
                      required
                      className={`${inputClass} uppercase`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                  {/* Nationalität */}
                  <div>
                    <label htmlFor="booking_nationality" className={labelClass}>
                      {isDe ? "Nationalität" : "Nationality"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <NationalitySelect
                      id="booking_nationality"
                      name="nationality"
                      isDe={isDe}
                      className={inputClass}
                    />
                  </div>

                  {/* Sprache Reiseleitung */}
                  <div>
                    <label htmlFor="booking_guidelang" className={labelClass}>
                      {isDe ? "Sprache für Reiseleitung" : "Guide language"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <select
                      id="booking_guidelang"
                      name="guidelang"
                      required
                      className={`${inputClass} cursor-pointer`}
                    >
                      <option value="">{isDe ? "Bitte wählen …" : "Please select …"}</option>
                      <option value="de">{isDe ? "Deutsch" : "German"}</option>
                      <option value="en">{isDe ? "Englisch" : "English"}</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                  {/* E-Mail */}
                  <div>
                    <label htmlFor="booking_email" className={labelClass}>
                      {isDe ? "E-Mail-Adresse" : "Email address"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="booking_email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="sie@beispiel.de"
                      className={inputClass}
                    />
                  </div>

                  {/* Telefon */}
                  <div>
                    <label htmlFor="booking_phone" className={labelClass}>
                      {isDe ? "Telefon / WhatsApp" : "Phone / WhatsApp"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="booking_phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="+62 81 246 21 8000"
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* Nachricht */}
                <div className="mt-5">
                  <label htmlFor="booking_message" className={labelClass}>
                    {isDe ? "Ihre Nachricht / besondere Wünsche" : "Your message / special requests"}
                  </label>
                  <textarea
                    id="booking_message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder={isDe
                      ? "Gibt es etwas, das wir wissen sollten? Ernährung, Anreisezeit, besondere Wünsche..."
                      : "Is there anything we should know? Diet, arrival time, special requests..."}
                    className={`${inputClass} resize-none`}
                  />
                  <p className="text-xs text-foreground-500 mt-1">
                    {isDe ? "Maximal 500 Zeichen" : "Maximum 500 characters"}
                  </p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  className="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {/* AGB Checkbox */}
                <div className="mt-6 flex items-start gap-3">
                  <input
                    id="booking_terms"
                    name="terms"
                    type="checkbox"
                    value="1"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400 cursor-pointer"
                  />
                  <label htmlFor="booking_terms" className="text-sm text-foreground-700 leading-relaxed cursor-pointer">
                    {isDe ? (
                      <>
                        Ich habe die{" "}
                        <LocalizedLink
                          to="/impressum"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          AGB
                        </LocalizedLink>{" "}
                        gelesen und akzeptiere sie.
                      </>
                    ) : (
                      <>
                        I have read and accept the{" "}
                        <LocalizedLink
                          to="/impressum"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          terms and conditions
                        </LocalizedLink>
                        .
                      </>
                    )}
                    <span className="text-accent-600 ml-1">*</span>
                  </label>
                </div>

                {/* Datenschutz Checkbox */}
                <div className="mt-4 flex items-start gap-3">
                  <input
                    id="booking_privacy"
                    name="privacy"
                    type="checkbox"
                    value="1"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400 cursor-pointer"
                  />
                  <label htmlFor="booking_privacy" className="text-sm text-foreground-700 leading-relaxed cursor-pointer">
                    {isDe ? (
                      <>
                        Ich habe die{" "}
                        <LocalizedLink
                          to="/privacy"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          Datenschutzerklärung
                        </LocalizedLink>{" "}
                        gelesen und bin mit der Verarbeitung meiner Angaben zur Beantwortung der Anfrage einverstanden.
                      </>
                    ) : (
                      <>
                        I have read the{" "}
                        <LocalizedLink
                          to="/privacy"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          privacy policy
                        </LocalizedLink>{" "}
                        and agree to the processing of my data for the purpose of responding to my inquiry.
                      </>
                    )}
                    <span className="text-accent-600 ml-1">*</span>
                  </label>
                </div>

                {/* Submit */}
                <div className="mt-8">
                  {tourValue && roundtripKey && (
                    <div className="mb-5 bg-background-100 border border-background-200 rounded-lg px-5 py-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <i className="ri-group-line text-accent-700"></i>
                          <span className="text-sm font-medium text-foreground-800">
                            {isDe ? "Anzahl Personen" : "Number of persons"}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-foreground-950">
                          {isDe
                            ? `${adults} Erwachsene${children > 0 ? ` + ${children} Kinder` : ""}`
                            : `${adults} adults${children > 0 ? ` + ${children} children` : ""}`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-t border-background-200 pt-3">
                        <div className="flex items-center gap-2">
                          <i className="ri-wallet-3-line text-accent-700"></i>
                          <span className="text-sm font-semibold text-foreground-950">
                            {isDe ? "Gesamtpreis" : "Total price"}
                          </span>
                        </div>
                        <span className="font-heading text-lg font-semibold text-accent-700">
                          {money(totalPrice)}
                        </span>
                      </div>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={status.type === "sending"}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-medium px-10 py-3.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                  >
                    {status.type === "sending" && (
                      <i className="ri-loader-4-line animate-spin"></i>
                    )}
                    {status.type === "sending"
                      ? (isDe ? "Wird gesendet..." : "Sending...")
                      : (isDe ? "Absenden" : "Submit")}
                  </button>
                  <BookingCompanyNotice isDe={isDe} />
                </div>

                {/* Status messages */}
                {status.type === "success" && (
                  <div className="mt-6 text-sm font-medium text-primary-700 bg-primary-100 border border-primary-200 rounded-md px-4 py-3">
                    {status.msg}
                  </div>
                )}
                {status.type === "error" && (
                  <div className="mt-6 text-sm font-medium text-secondary-700 bg-secondary-100 border border-secondary-200 rounded-md px-4 py-3">
                    {status.msg}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}